import {AfterViewInit, Component, ElementRef, OnDestroy} from "@angular/core";
import {IHeaderGroupAngularComp} from "ag-grid-angular";
import {EventEmitter, Output} from "@angular/core";
import {PlanDetailHeaderBase} from "../base/plan-detail-header";
import {IHeaderParams} from 'ag-grid-community';

// ESP-18515: upper bound on the number of animation frames to wait for AG Grid
// to insert this collapse header under its live `.ag-header-group-cell` (~0.5s
// at 60fps). Attachment normally happens within 1–2 frames; the bound simply
// prevents an unbounded loop if the group cell never materialises (e.g. the
// column is destroyed before it is ever attached).
const MAX_ATTACH_FRAMES = 30;

@Component({
  standalone: false,
  selector: "wfm-esp-plan-detail-header-row",
  templateUrl: "./plan-detail-header-row.component.html",
  styleUrls: ["./plan-detail-header-row.component.scss"]
})
export class PlanDetailHeaderRowComponent extends PlanDetailHeaderBase implements IHeaderGroupAngularComp, AfterViewInit, OnDestroy {

  private collapsed = false;
  private headerGroupCell: HTMLElement | null = null;
  private attachFrame: number | null = null;

  @Output()
  public drilldownIconClicked: EventEmitter<boolean> = new EventEmitter<boolean>();

  /**
   * WCAG 2.1.1 Keyboard / 2.4.7 Focus Visible (ESP-18504 reopen): AG Grid
   * manages keyboard focus at the header GROUP CELL level (`.ag-header-group-cell`
   * is `tabindex="-1"` and AG Grid calls `.focus()` on it as you Tab across the
   * header row). That left the "-" collapse group cell as a focus stop BEFORE
   * its inner <button>. Whenever AG Grid focuses THIS group cell, hand focus
   * straight to the inner collapse <button> so the button is the single, direct
   * Tab stop with one visible ring. Mirrors the "+" expand header
   * (PlanDetailClickableHeaderComponent).
   */
  private readonly forwardFocusToButton = (event: FocusEvent): void => {
    if (this.headerGroupCell && event.target === this.headerGroupCell && !this.isDisabled) {
      const button = this.headerGroupCell.querySelector<HTMLButtonElement>("button.customHeaderLabel");
      button?.focus();
    }
  };

  /**
   * WCAG 4.1.2 Name, Role, Value / Dragon voice control (ESP-18504 reopen): AG
   * Grid renders each expanded month header inside a `.ag-header-group-cell`
   * with `role="columnheader"` whose accessible name is the date label (e.g.
   * "Mar 2024"). Dragon matches that name against "click Mar 2024" and clicks
   * the CENTRE of the group cell — but the "-" collapse `<button>` only spans a
   * narrow strip on the LEFT of the ~440px-wide cell (live-verified: the cell
   * centre lands on bare `.ag-header-group-cell` chrome, not the button), so the
   * click activates nothing and the column cannot be collapsed by voice. That is
   * the reported "expanded using Dragon but unable to collapse" symptom. Forward
   * any click that lands on the group cell — but NOT on the button itself or AG
   * Grid's own resize/menu chrome — to the collapse toggle, so every expanded
   * month is reliably collapsible by voice. The button keeps handling its own
   * clicks, so this never double-fires.
   */
  private readonly forwardClickToCollapse = (event: MouseEvent): void => {
    if (this.isDisabled) return;
    const target = event.target as HTMLElement | null;
    if (!target) return;
    // The <button> already handles pointer/keyboard activation itself.
    if (target.closest("button.customHeaderLabel")) return;
    // Leave AG Grid's own header controls (column resize handle, menu/filter
    // buttons) alone so resizing/menu clicks don't trigger a collapse.
    if (target.closest(".ag-header-cell-resize, .ag-header-cell-menu-button, .ag-header-cell-filter-button")) {
      return;
    }
    this.toggleCollapsed();
  };

  constructor(private readonly elementRef: ElementRef<HTMLElement>) {
    super();
  }

  ngAfterViewInit(): void {
    // WCAG 4.1.2 / Dragon voice control (ESP-18504 reopen): AG Grid Angular
    // builds this component against a DETACHED `eGui` element and only inserts
    // it into the live `.ag-header-group-cell` AFTER Angular's own
    // `ngAfterViewInit` has fired. Looking up `closest(".ag-header-group-cell")`
    // synchronously here therefore returns null, so deferring to an animation
    // frame runs this after AG Grid has attached the group cell to the real
    // header row (mirrors the "+" expand header's timing fix).
    //
    // ESP-18515 (reopen): AG Grid does NOT guarantee the group cell is attached
    // within a SINGLE animation frame. Expanded months realised later in a
    // layout pass — the reported "May 2020" that received focus but could not be
    // collapsed by voice — are inserted a few frames later, so a one-shot rAF
    // snapshot resolved to null and that month's collapse forwarding never
    // attached. Poll across a bounded number of frames until the group cell
    // exists so EVERY expanded month is collapsible by voice.
    this.attachToHeaderGroupCell(MAX_ATTACH_FRAMES);
  }

  private attachToHeaderGroupCell(remainingFrames: number): void {
    this.attachFrame = requestAnimationFrame(() => {
      const cell = this.elementRef.nativeElement.closest<HTMLElement>(".ag-header-group-cell");
      if (cell) {
        this.headerGroupCell = cell;
        this.headerGroupCell.addEventListener("focusin", this.forwardFocusToButton);
        this.headerGroupCell.addEventListener("click", this.forwardClickToCollapse);
        return;
      }
      if (remainingFrames > 1) {
        this.attachToHeaderGroupCell(remainingFrames - 1);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.attachFrame != null) {
      cancelAnimationFrame(this.attachFrame);
    }
    this.headerGroupCell?.removeEventListener("focusin", this.forwardFocusToButton);
    this.headerGroupCell?.removeEventListener("click", this.forwardClickToCollapse);
    this.headerGroupCell = null;
  }

  get isCollapsed() {
    return this.collapsed;
  }
  get isDisabled(){
    let params:any = this.params;

    //plan detail v2 doesn't have grid validity
    if(params.context.isGridValid && params.context.isGridValid()==false){
      return true;
    }

    return false;

  }
  toggleCollapsed() {
    let params:any = this.params;

    if (this.isDisabled) {
      return;
    }

    this.collapsed = !this.collapsed;
    this.drilldownIconClicked.emit(this.collapsed);

    params.context.updateViewMode(this.collapsed, params.columnGroup.children[0]);

  }
}
